import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import { personApi, type PersonById } from '../services/personApi';
import {
    PARTY_ROLE_OPTIONS,
    PartyRole,
    type Party,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Resolve nome/documento das partes que têm personId (ADR 001: o recovery não copia o dado
// do person, resolve na leitura). Dedup por personId; falha de uma pessoa não derruba as
// demais. Só busca ids ainda não resolvidos, para não repetir chamada a cada refresh.
const resolveMissingPeople = async (
    parties: Party[],
    known: Record<string, PersonById>,
    signal: AbortSignal,
): Promise<Record<string, PersonById>> => {
    const missingIds = Array.from(
        new Set(
            parties
                .filter((p) => p.personId && !known[p.personId])
                .map((p) => p.personId as string),
        ),
    );
    if (missingIds.length === 0) return known;

    const resolvedList = await Promise.all(
        missingIds.map((id) => personApi.resolvePersonById(id, signal).catch(() => null)),
    );

    const next = { ...known };
    resolvedList.forEach((person) => {
        if (person) next[person.personId] = person;
    });
    return next;
};

// Gerencia as partes de um caso: lista, adiciona e remove. Fetch próprio pelo id do caso,
// para o painel de detalhe atualizar sem recarregar o page-view inteiro.
export default function useLegalCaseParties(caseId: string | null, initialParties?: Party[]) {
    const [parties, setParties] = useState<Party[]>(initialParties || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [roleOptions, setRoleOptions] = useState<SelectOption[]>(PARTY_ROLE_OPTIONS);
    const [resolvedPeople, setResolvedPeople] = useState<Record<string, PersonById>>({});
    // Guarda o caso já semeado. `initialParties` chega como referência nova a cada render
    // (o painel passa `detail.parties`), então NÃO pode entrar nas deps do efeito de reset —
    // faria loop infinito (setState → re-render → nova ref → efeito de novo). O reset ocorre
    // só quando o caso muda de fato.
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação e sobrescrever a lista com o snapshot antigo. Cada fetch captura a geração
    // ao iniciar e só aplica o resultado se ainda for a corrente (o próprio ++ invalida os em voo).
    const fetchGenRef = useRef(0);

    const fetchParties = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getParties(caseId);
            if (fetchGenRef.current === generation) setParties(result.items || []);
        } catch {
            // silencioso — o painel já mostra as partes que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setParties(initialParties || []);
        setErrorFields([]);
        // Semeia com o page-view (paint imediato) e re-busca do servidor. O re-fetch cobre a
        // remontagem da seção (ao alternar de aba): sem ele, voltaria ao `initialParties` do
        // page-view — carregado antes de qualquer inclusão — e o registro recém-criado
        // "sumiria" até recarregar a página. initialParties fora das deps de propósito.
        fetchParties();
        setResolvedPeople({});
        // initialParties fora das deps de propósito (referência nova a cada render). O
        // seed é por caso; add/remove atualizam via fetchParties.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    // Resolve nome/documento das partes com personId. Roda a cada mudança de `parties`,
    // mas só busca os ids ainda desconhecidos. AbortController cancela em unmount/troca.
    useEffect(() => {
        const controller = new AbortController();
        resolveMissingPeople(parties, resolvedPeople, controller.signal)
            .then((next) => {
                if (next !== resolvedPeople) setResolvedPeople(next);
            })
            .catch(() => {
                // silencioso — sem o nome, a lista ainda mostra papel e documento local
            });
        return () => controller.abort();
        // resolvedPeople fora das deps de propósito: resolveMissingPeople já lê o valor
        // atual e só busca o que falta; incluí-lo criaria loop de re-fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parties]);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.partyRoles?.length) setRoleOptions(opts.partyRoles);
            } catch {
                // fallback local já preenche
            }
        };
        loadRoles();
    }, []);

    const addParty = async (data: {
        role: PartyRole;
        personId: string;
        name: string;
        document: string;
        description: string;
    }): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addParty(caseId, {
                role: data.role || PartyRole.DEBTOR,
                personId: data.personId || undefined,
                name: data.name || undefined,
                document: data.document || undefined,
                description: data.description || undefined,
            });
            await fetchParties();
            toast.success('Parte adicionada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao adicionar parte');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const clearErrors = () => setErrorFields([]);

    const removeParty = async (partyId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeParty(caseId, partyId);
            await fetchParties();
            toast.success('Parte removida');
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao remover parte');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        parties, isLoading, isSaving, errorFields, roleOptions,
        resolvedPeople, addParty, removeParty, clearErrors,
    };
}
